import React, { FormEvent, useEffect, useMemo, useState } from 'react'
import {
  ArrowLeft,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Mail,
  RotateCcw,
  Sparkles,
} from 'lucide-react'

type BookedSlots = Record<string, string[]>

const TIME_ZONE = 'America/Chicago'
const TIME_ZONE_LABEL = 'Central Time'
const AUDIT_DURATION_MINUTES = 18
const STORAGE_KEY = 'zadro-circuit-audit-booked-slots-v2'

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const availableTimeSlots = [
  '09:00',
  '09:20',
  '09:40',
  '10:00',
  '10:20',
  '10:40',
  '11:00',
  '11:20',
  '11:40',
  '14:00',
  '14:20',
  '14:40',
  '15:00',
  '15:20',
  '15:40',
  '16:00',
  '16:20',
  '16:40',
]

const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate()

const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay()

const pad = (value: number) => String(value).padStart(2, '0')

const makeDateKey = (year: number, month: number, day: number) => `${year}-${pad(month + 1)}-${pad(day)}`

const formatTime = (time24: string) => {
  const [hours, minutes] = time24.split(':')
  const hoursInt = Number.parseInt(hours, 10)
  const ampm = hoursInt >= 12 ? 'PM' : 'AM'
  const formattedHours = hoursInt % 12 || 12
  return `${formattedHours}:${minutes} ${ampm}`
}

const getPartsInTimeZone = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date)

  const value = (type: string) => parts.find((part) => part.type === type)?.value || '00'

  return {
    year: Number(value('year')),
    month: Number(value('month')),
    day: Number(value('day')),
    hour: Number(value('hour')),
    minute: Number(value('minute')),
  }
}

const getTodayKey = () => {
  const now = getPartsInTimeZone()
  return `${now.year}-${now.month}-${now.day}`.replace(/-(\d)(?=-|$)/g, '-0$1')
}

const getCurrentTimeInMinutes = () => {
  const now = getPartsInTimeZone()
  return now.hour * 60 + now.minute
}

const getSlotTimeInMinutes = (slot: string) => {
  const [hours, minutes] = slot.split(':').map(Number)
  return hours * 60 + minutes
}

const formatDateLabel = (dateKey: string, includeWeekday = true) => {
  const [year, month, day] = dateKey.split('-').map(Number)
  return new Intl.DateTimeFormat('en-US', {
    weekday: includeWeekday ? 'long' : undefined,
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)))
}

const getCalendarMonthFromCentralTime = () => {
  const now = getPartsInTimeZone()
  return new Date(now.year, now.month - 1, 1)
}

const buildPreCallBrief = ({
  name,
  companyName,
  role,
  biggestChallenge,
}: {
  name: string
  companyName: string
  role: string
  biggestChallenge: string
}) => {
  const normalized = biggestChallenge.toLowerCase()
  const likelyIssues = []

  if (normalized.includes('ai') || normalized.includes('automation') || normalized.includes('llm')) {
    likelyIssues.push('AI workflow selection may be moving faster than governance, data readiness, or production ownership.')
  }

  if (
    normalized.includes('team') ||
    normalized.includes('engineer') ||
    normalized.includes('developer') ||
    normalized.includes('communication')
  ) {
    likelyIssues.push('There may be a translation gap between business intent, technical execution, and delivery rhythm.')
  }

  if (
    normalized.includes('slow') ||
    normalized.includes('performance') ||
    normalized.includes('conversion') ||
    normalized.includes('platform')
  ) {
    likelyIssues.push('The highest-value issue may be a platform bottleneck that is quietly leaking customer trust or revenue.')
  }

  if (
    normalized.includes('mvp') ||
    normalized.includes('roadmap') ||
    normalized.includes('scope') ||
    normalized.includes('launch')
  ) {
    likelyIssues.push('The roadmap may need a tighter validation loop so the team can ship smaller, learn faster, and reduce rework.')
  }

  if (likelyIssues.length === 0) {
    likelyIssues.push('The first useful move is to separate the visible symptom from the system constraint underneath it.')
    likelyIssues.push('We should identify whether this is a people, process, platform, or prioritization issue before prescribing tools.')
  }

  return `Pre-call brief for ${name} at ${companyName}

Primary signal:
You are coming in as ${role} with this challenge: "${biggestChallenge}"

Potential circuit issues:
- ${likelyIssues.slice(0, 2).join('\n- ')}

Questions we should answer in the ${AUDIT_DURATION_MINUTES}-minute audit:
- Where does this problem first show up: customer experience, team delivery, data flow, or executive decision-making?
- What has already been tried, and where did momentum stall?
- If we could fix only one constraint in the next 30 days, which one would unlock the most measurable value?`
}

const buildMailtoHref = ({
  name,
  email,
  companyName,
  role,
  biggestChallenge,
  selectedDateKey,
  selectedTime,
  analysisResult,
}: {
  name: string
  email: string
  companyName: string
  role: string
  biggestChallenge: string
  selectedDateKey: string
  selectedTime: string
  analysisResult: string
}) => {
  const subject = `Circuit Audit Request - ${companyName}`
  const body = `Hi Juan,

I would like to request a Circuit Audit consultation.

Requested time:
${formatDateLabel(selectedDateKey)} at ${formatTime(selectedTime)} ${TIME_ZONE_LABEL}

Name: ${name}
Email: ${email}
Company: ${companyName}
Role: ${role}

Biggest challenge:
${biggestChallenge}

Pre-call brief:
${analysisResult}
`

  return `mailto:juan.cubeddu@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

const buildCalendarHref = (selectedDateKey: string, selectedTime: string, companyName: string) => {
  const compactDate = selectedDateKey.replaceAll('-', '')
  const compactStart = selectedTime.replace(':', '')
  const endMinutes = getSlotTimeInMinutes(selectedTime) + AUDIT_DURATION_MINUTES
  const compactEnd = `${pad(Math.floor(endMinutes / 60))}${pad(endMinutes % 60)}`
  const body = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Zadro//Circuit Audit//EN',
    'BEGIN:VEVENT',
    `UID:zadro-circuit-audit-${compactDate}-${compactStart}@zadro.tech`,
    `DTSTART;TZID=${TIME_ZONE}:${compactDate}T${compactStart}00`,
    `DTEND;TZID=${TIME_ZONE}:${compactDate}T${compactEnd}00`,
    `SUMMARY:Zadro Circuit Audit - ${companyName}`,
    'DESCRIPTION:Complimentary 18-minute Circuit Audit consultation with Juan Cubeddu.',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n')

  return `data:text/calendar;charset=utf-8,${encodeURIComponent(body)}`
}

export function CircuitAuditScheduler() {
  const [currentMonth, setCurrentMonth] = useState(getCalendarMonthFromCentralTime)
  const [selectedDateKey, setSelectedDateKey] = useState(getTodayKey)
  const [selectedTime, setSelectedTime] = useState<string | null>(null)
  const [step, setStep] = useState(1)
  const [bookedSlots, setBookedSlots] = useState<BookedSlots>({})
  const [analysisResult, setAnalysisResult] = useState('')

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [role, setRole] = useState('')
  const [biggestChallenge, setBiggestChallenge] = useState('')

  const year = currentMonth.getFullYear()
  const month = currentMonth.getMonth()
  const daysInMonth = getDaysInMonth(year, month)
  const firstDay = getFirstDayOfMonth(year, month)
  const todayKey = getTodayKey()

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY)
      if (stored) {
        setBookedSlots(JSON.parse(stored))
      }
    } catch {
      setBookedSlots({})
    }
  }, [])

  const selectedDateLabel = useMemo(() => formatDateLabel(selectedDateKey), [selectedDateKey])

  const todaysBookedSlots = bookedSlots[selectedDateKey] || []

  const isDisabledDay = (day: number) => {
    const date = new Date(year, month, day)
    const dateKey = makeDateKey(year, month, day)
    const isWeekend = date.getDay() === 0 || date.getDay() === 6
    return dateKey < todayKey || isWeekend
  }

  const isDisabledSlot = (slot: string) => {
    if (todaysBookedSlots.includes(slot)) return true
    if (selectedDateKey !== todayKey) return false
    return getSlotTimeInMinutes(slot) <= getCurrentTimeInMinutes()
  }

  const persistBookedSlot = () => {
    if (!selectedTime) return

    const updated = {
      ...bookedSlots,
      [selectedDateKey]: [...(bookedSlots[selectedDateKey] || []), selectedTime],
    }

    setBookedSlots(updated)
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
  }

  const handleDateClick = (day: number) => {
    if (isDisabledDay(day)) return
    setSelectedDateKey(makeDateKey(year, month, day))
    setSelectedTime(null)
  }

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time)
    setStep(2)
  }

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!selectedTime) return

    setStep(3)

    const brief = buildPreCallBrief({
      name,
      companyName,
      role,
      biggestChallenge,
    })

    window.setTimeout(() => {
      persistBookedSlot()
      setAnalysisResult(brief)
      setStep(4)
    }, 550)
  }

  const startNewBooking = () => {
    setSelectedTime(null)
    setName('')
    setEmail('')
    setCompanyName('')
    setRole('')
    setBiggestChallenge('')
    setAnalysisResult('')
    setStep(1)
  }

  const mailtoHref =
    selectedTime && analysisResult
      ? buildMailtoHref({
          name,
          email,
          companyName,
          role,
          biggestChallenge,
          selectedDateKey,
          selectedTime,
          analysisResult,
        })
      : '#'

  const calendarHref =
    selectedTime && companyName ? buildCalendarHref(selectedDateKey, selectedTime, companyName) : '#'

  return (
    <div className="relative overflow-hidden rounded-lg border border-slate-700/80 bg-slate-900/85 text-white shadow-2xl shadow-sky-950/30">
      <div className="absolute inset-0 scheduler-scanline opacity-70" aria-hidden="true" />
      <div className="relative border-b border-slate-700/80 p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-300">Circuit Audit</p>
            <h2 className="mt-3 text-3xl font-black text-white">Schedule your diagnostic call</h2>
            <p className="mt-3 max-w-2xl leading-7 text-slate-300">
              Book a complimentary {AUDIT_DURATION_MINUTES}-minute consultation. Times are shown in {TIME_ZONE_LABEL}
              , with a 2-minute buffer between calls.
            </p>
          </div>
          <div className="rounded-md border border-slate-700 bg-slate-950/70 px-4 py-3 text-sm text-slate-300">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-sky-300" />
              {AUDIT_DURATION_MINUTES} minutes
            </div>
            <div className="mt-2 flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-sky-300" />
              Weekdays only
            </div>
          </div>
        </div>
      </div>

      <div className="relative min-h-[520px] p-4 md:p-6">
        {step === 1 && (
          <div className="grid gap-8 lg:grid-cols-[1fr_0.9fr]">
            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
              <div className="mb-5 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}
                  className="rounded-md border border-slate-700 p-2 text-slate-200 transition hover:border-sky-300 hover:text-sky-200"
                  aria-label="Previous month"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <h3 className="text-xl font-bold text-white">
                  {monthNames[month]} {year}
                </h3>
                <button
                  type="button"
                  onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}
                  className="rounded-md border border-slate-700 p-2 text-slate-200 transition hover:border-sky-300 hover:text-sky-200"
                  aria-label="Next month"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-2 text-center text-sm text-slate-400">
                {dayNames.map((day) => (
                  <div key={day} className="pb-2 font-semibold">
                    {day}
                  </div>
                ))}
                {Array.from({ length: firstDay }).map((_, index) => (
                  <div key={`empty-${index}`} className="aspect-square" />
                ))}
                {Array.from({ length: daysInMonth }).map((_, index) => {
                  const day = index + 1
                  const dateKey = makeDateKey(year, month, day)
                  const isSelected = dateKey === selectedDateKey
                  const disabled = isDisabledDay(day)

                  return (
                    <button
                      key={dateKey}
                      type="button"
                      disabled={disabled}
                      onClick={() => handleDateClick(day)}
                      className={`aspect-square rounded-md text-sm font-semibold transition ${
                        disabled
                          ? 'cursor-not-allowed text-slate-700'
                          : isSelected
                            ? 'bg-sky-300 text-slate-950 shadow-lg shadow-sky-400/20'
                            : 'text-slate-200 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      {day}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
              <h3 className="text-xl font-bold text-white">{selectedDateLabel}</h3>
              <p className="mt-2 text-sm text-slate-400">{TIME_ZONE_LABEL} · 18-minute audits</p>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {availableTimeSlots.map((time) => {
                  const disabled = isDisabledSlot(time)

                  return (
                    <button
                      key={time}
                      type="button"
                      disabled={disabled}
                      onClick={() => handleTimeSelect(time)}
                      className={`rounded-md border p-3 text-center text-sm font-semibold transition ${
                        disabled
                          ? 'cursor-not-allowed border-slate-800 bg-slate-900 text-slate-600'
                          : 'border-slate-700 text-slate-100 hover:border-sky-300 hover:bg-sky-300 hover:text-slate-950'
                      }`}
                    >
                      {formatTime(time)}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {step === 2 && selectedTime && (
          <div className="relative mx-auto max-w-2xl rounded-lg border border-slate-800 bg-slate-950/60 p-6">
            <button
              type="button"
              onClick={() => {
                setSelectedTime(null)
                setStep(1)
              }}
              className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-300 transition hover:text-sky-200"
            >
              <ArrowLeft className="h-4 w-4" />
              Choose another time
            </button>
            <h3 className="text-2xl font-black text-white">Provide details for your audit</h3>
            <p className="mt-3 leading-7 text-slate-300">
              You are requesting {formatTime(selectedTime)} {TIME_ZONE_LABEL} on{' '}
              <span className="font-semibold text-sky-300">{selectedDateLabel}</span>.
            </p>

            <form onSubmit={handleFormSubmit} className="mt-6 grid gap-4">
              <input
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                className="scheduler-input"
              />
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                className="scheduler-input"
              />
              <input
                type="text"
                placeholder="Company name"
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
                required
                className="scheduler-input"
              />
              <input
                type="text"
                placeholder="Your role, e.g. Founder, CEO, Head of Product"
                value={role}
                onChange={(event) => setRole(event.target.value)}
                required
                className="scheduler-input"
              />
              <textarea
                placeholder="What is the single biggest challenge you want to diagnose?"
                value={biggestChallenge}
                onChange={(event) => setBiggestChallenge(event.target.value)}
                required
                rows={5}
                className="scheduler-input resize-none"
              />
              <button
                type="submit"
                className="mt-2 rounded-md bg-sky-300 px-5 py-3 font-bold text-slate-950 transition hover:bg-sky-200"
              >
                Confirm request and generate brief
              </button>
            </form>
          </div>
        )}

        {step === 3 && (
          <div className="flex min-h-[460px] flex-col items-center justify-center text-center">
            <Sparkles className="h-12 w-12 animate-pulse text-sky-300" />
            <h3 className="mt-5 text-2xl font-black text-white">Analyzing your circuit...</h3>
            <p className="mt-3 max-w-md leading-7 text-slate-300">
              Preparing a pre-call brief from your intake so the audit starts with focus.
            </p>
          </div>
        )}

        {step === 4 && selectedTime && (
          <div className="mx-auto max-w-3xl rounded-lg border border-slate-800 bg-slate-950/60 p-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300">
              <Check className="h-8 w-8" />
            </div>
            <h3 className="mt-5 text-3xl font-black text-white">Audit request ready</h3>
            <p className="mx-auto mt-3 max-w-2xl leading-7 text-slate-300">
              {name}, your selected time is {formatTime(selectedTime)} {TIME_ZONE_LABEL} on{' '}
              <span className="font-semibold text-sky-300">{selectedDateLabel}</span>. Send the request
              to Juan and add the hold to your calendar.
            </p>

            <div className="mt-6 rounded-lg border border-slate-800 bg-slate-900 p-5 text-left">
              <h4 className="text-lg font-bold text-sky-300">Pre-call brief</h4>
              <pre className="mt-4 whitespace-pre-wrap font-sans text-sm leading-7 text-slate-300">
                {analysisResult}
              </pre>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <a
                href={mailtoHref}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-sky-300 px-5 py-3 font-bold text-slate-950 transition hover:bg-sky-200"
              >
                <Mail className="h-4 w-4" />
                Send request
              </a>
              <a
                href={calendarHref}
                download="zadro-circuit-audit.ics"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-700 px-5 py-3 font-bold text-slate-100 transition hover:border-sky-300 hover:text-sky-200"
              >
                <Download className="h-4 w-4" />
                Add calendar hold
              </a>
              <button
                type="button"
                onClick={startNewBooking}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-700 px-5 py-3 font-bold text-slate-100 transition hover:border-sky-300 hover:text-sky-200"
              >
                <RotateCcw className="h-4 w-4" />
                Start over
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
